import { useState, useContext, useEffect } from 'react';
import classnames from 'classnames';
import { ReactRelayContext, createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { getBuyerId } from 'dibs-cookie-jar';
import { Input } from 'dibs-elements/exports/Input';
import { Button } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { trackRegistrationEntry } from '../../../authModal/authentication/authTracking';
import authCopy from '../../../authModal/authentication/authCopy';
import { PrivacyPolicyLink, UserAgreementLink } from '../../../authModal/shared/Legal';
import { subscribeUserMutation } from './subscribeUserMutation';
import { registrationOrLoginHandler } from '../../../authModal/authentication/registrationOrLoginHandler';
import { SAVE_SUBSCRIPTION_PREFERENCES_FLOW } from '../../../authModal/authentication/authFlowConstants';

import styles from './styles.scss';

const GA_LABEL = 'footer navigation';

const messages = defineMessages({
    emailPlaceholder: {
        id: 'dbl.footer.FooterSubscribe.emailPlaceholder',
        defaultMessage: "Yes, here's my email",
    },
});

export const FooterSubscribeComponent = ({ isMobile, viewer, isNavigationRefreshVariant }) => {
    const intl = useIntl();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSubscribeComponent, setShowSubscribeComponent] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [hasFiredEntryTracking, setHasFiredEntryTracking] = useState(false);
    const { environment } = useContext(ReactRelayContext) || {};

    function registerAndSubscribe(e) {
        e.preventDefault();

        registrationOrLoginHandler({
            email,
            errorHandler: setErrorMessage,
            loadingHandler: setIsLoading,
            userRegistrationHandler: () => setShowConfirmation(true),
            userLoginHandler: res => {
                if (res.userId) {
                    subscribeUserMutation(environment, res.userId)
                        .then(() => {
                            setShowConfirmation(true);
                        })
                        .catch(() => setErrorMessage(authCopy.errors.support));
                } else {
                    setIsLoading(false);
                }
            },
            environment,
            gaLabel: GA_LABEL,
            flow: SAVE_SUBSCRIPTION_PREFERENCES_FLOW,
        });
    }

    function trackEntry() {
        if (!hasFiredEntryTracking) {
            trackRegistrationEntry({ label: GA_LABEL });
            setHasFiredEntryTracking(true);
        }
    }

    useEffect(() => {
        if (get(viewer, 'user.serviceId') && !isLoading) {
            setShowSubscribeComponent(false);
        }
    }, [viewer, isLoading]);
    useEffect(() => {
        setShowSubscribeComponent(!getBuyerId(document.cookie));
    }, []);

    const subscribeClassName = classnames(styles.footerSubscribe, {
        [styles.navigationRefreshSubscribe]: isNavigationRefreshVariant,
    });

    return (
        showSubscribeComponent && (
            <div className={subscribeClassName}>
                {showConfirmation ? (
                    <FormattedMessage
                        id="dbl.footer.FooterSubscribe.confirmation"
                        defaultMessage="Thanks for subscribing!"
                    />
                ) : (
                    <>
                        <form className={styles.footerForm} onSubmit={registerAndSubscribe}>
                            <p
                                className={classnames(
                                    styles.subscribeText,
                                    styles.subscribeDescription
                                )}
                            >
                                <FormattedMessage
                                    id="dbl.footer.FooterSubscribe.subheader"
                                    defaultMessage="Sign up for weekly emails with new listings, sales and more!"
                                />
                            </p>
                            <div className={styles.inputWrapper}>
                                <Input
                                    dataTn="footer-subscribe-input"
                                    className={styles.input}
                                    size={isMobile ? 'medium' : 'small'}
                                    horizontalSpacing="small"
                                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    onFocus={trackEntry}
                                    errorMessage={
                                        errorMessage ? (
                                            <div className={styles.error}>{errorMessage}</div>
                                        ) : null
                                    }
                                    type="email"
                                />
                            </div>
                            <Button
                                type="secondary"
                                className={styles.button}
                                dataTn="footer-subscribe-submit"
                                size={isMobile ? 'medium' : 'small'}
                            >
                                <div className={styles.innerButtonContent}>
                                    {isLoading ? (
                                        <Spinner containerClass={styles.spinner} size="tiny" />
                                    ) : (
                                        <div className={styles.follow}>
                                            <FormattedMessage
                                                id="dbl.footer.FooterSubscribe.button"
                                                defaultMessage="Subscribe"
                                            />
                                        </div>
                                    )}
                                </div>
                            </Button>
                        </form>
                        <div className={styles.subscribeText}>
                            <FormattedMessage
                                id="dbl.footer.FooterSubscribe.gdprText"
                                defaultMessage="By entering your email, you agree to our {userAgreementLink} and {privacyPolicyLink}"
                                values={{
                                    userAgreementLink: <UserAgreementLink />,
                                    privacyPolicyLink: <PrivacyPolicyLink />,
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        )
    );
};

FooterSubscribeComponent.propTypes = {
    isMobile: PropTypes.bool,
    isNavigationRefreshVariant: PropTypes.bool,
    viewer: PropTypes.object,
};

const mapStateToProps = ({ header }) => ({
    isMobile: header.isMobile,
    isNavigationRefreshVariant: header.isNavigationRefreshVariant,
});

export default createFragmentContainer(connect(mapStateToProps)(FooterSubscribeComponent), {
    viewer: graphql`
        fragment FooterSubscribe_viewer on Viewer {
            user(userId: $userId) @include(if: $hasUserId) {
                serviceId
            }
        }
    `,
});
